import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileText, PackagePlus, Search, ShoppingCart, X } from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Select } from '../components/ui/Select';
import { inputClass } from '../components/ui/Input';
import { formatDate, formatEUR } from '../lib/format';
import { enumLabel } from '../lib/enumLabel';
import { usePermission } from '../auth/usePermission';
import { Permission } from '../auth/permissions';

type Kind = 'PO' | 'INVOICE';

interface PurchaseRow {
  kind: Kind;
  id: string;
  number: string | null;
  supplierRef: string | null;
  date: string;
  supplierId: string;
  supplierName: string;
  supplierCode: string;
  channel: 'API' | 'MANUAL' | 'INVOICE_IMPORT';
  status: string;
  lineCount: number;
  units: number;
  totalHT: number | null;
  orderId: string | null;
}

interface InvoiceDetail {
  id: string;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  totalHT: string | null;
  createdAt: string;
  supplierName: string;
  supplierCode: string;
  importedBy: string | null;
  lines: Array<{
    id: string;
    ref: string | null;
    description: string | null;
    brand: string | null;
    quantity: number;
    unitPriceHT: number;
    productCreated: boolean;
    productId: string | null;
    productSku: string | null;
    productName: string | null;
    alreadyInStock: number;
    receivedHere: number;
    costBefore: number | null;
    costAfter: number | null;
    variancePct: number | null;
    varianceFlag: boolean;
    priceAcceptedBy: string | null;
    poNumbers: string[];
  }>;
}

/**
 * Purchases → "Achats" (2026-10-13): every purchase in one register — the purchase orders sent to
 * API suppliers (Auto Partner, Inter Cars) or by hand, and the invoices of suppliers with no API
 * received through "Import facture d'achat". A PO opens on the Commandes fournisseurs screen that
 * acts on it; an imported invoice opens here, read-only.
 */
export default function PurchasesRegister() {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const canImport = usePermission(Permission.STOCK_RECEIVE);
  const [rows, setRows] = useState<PurchaseRow[] | null>(null);
  const [kind, setKind] = useState<'' | Kind>('');
  const [supplierId, setSupplierId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState<InvoiceDetail | null>(null);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get<PurchaseRow[]>('/purchasing/register', {
        params: {
          kind: kind || undefined,
          supplierId: supplierId || undefined,
          from: from || undefined,
          to: to || undefined,
          q: q.trim() || undefined,
        },
      });
      setRows(data);
    } catch (err) {
      showApiError(err);
      setRows([]);
    }
  }, [kind, supplierId, from, to, q, showApiError]);

  useEffect(() => {
    const timer = setTimeout(() => void load(), 250);
    return () => clearTimeout(timer);
  }, [load]);

  const openInvoice = useCallback(
    async (id: string) => {
      try {
        const { data } = await apiClient.get<InvoiceDetail>(`/purchasing/register/invoices/${id}`);
        setDetail(data);
      } catch (err) {
        showApiError(err);
      }
    },
    [showApiError]
  );

  // Arriving from the import's "Open in purchases": show the record just written.
  const linked = params.get('invoice');
  useEffect(() => {
    if (linked) void openInvoice(linked);
  }, [linked, openInvoice]);

  // Supplier filter options come from what the register holds, not the supplier admin list.
  const suppliers = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of rows ?? []) m.set(r.supplierId, `${r.supplierName} (${r.supplierCode})`);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const totals = useMemo(() => {
    const list = rows ?? [];
    return {
      count: list.length,
      ht: list.reduce((s, r) => s + (r.totalHT ?? 0), 0),
    };
  }, [rows]);

  const open = (r: PurchaseRow) =>
    r.kind === 'PO' ? navigate(`/purchasing/orders?po=${r.id}`) : void openInvoice(r.id);

  return (
    <div>
      <PageHeader
        title={t('purchases.register.title')}
        description={t('purchases.register.description')}
        actions={
          canImport && (
            <Button size="sm" onClick={() => navigate('/purchasing/purchase-import')}>
              <PackagePlus className="h-4 w-4" /> {t('purchases.register.importInvoice')}
            </Button>
          )
        }
      />

      <div className="mb-3 rounded-lg border bg-card p-3 shadow-sm">
        <div className="flex flex-wrap items-end gap-2">
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t('purchases.register.kind')}
            </span>
            <Select value={kind} onChange={(e) => setKind(e.target.value as '' | Kind)}>
              <option value="">{t('purchases.register.allKinds')}</option>
              <option value="PO">{t('purchases.register.kind_PO')}</option>
              <option value="INVOICE">{t('purchases.register.kind_INVOICE')}</option>
            </Select>
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t('purchases.register.supplier')}
            </span>
            <Select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
              <option value="">{t('purchases.register.allSuppliers')}</option>
              {suppliers.map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </Select>
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t('purchases.register.from')}
            </span>
            <input
              type="date"
              className={inputClass}
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t('purchases.register.to')}
            </span>
            <input
              type="date"
              className={inputClass}
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </label>
          <label className="block min-w-[14rem] flex-1">
            <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t('purchases.register.search')}
            </span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <input
                className={`${inputClass} pl-8`}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('purchases.register.searchPlaceholder')}
              />
            </div>
          </label>
        </div>
      </div>

      <div className="rounded-lg border bg-card shadow-sm">
        <div className="flex items-center justify-between border-b px-4 py-2 text-sm">
          <span className="text-muted-foreground">
            {t('purchases.register.summary', { count: totals.count, total: formatEUR(totals.ht) })}
          </span>
        </div>
        <div className="overflow-auto">
          <table className="w-full border-collapse text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr className="border-b">
                <th className="px-3 py-2 font-medium">{t('purchases.register.colDate')}</th>
                <th className="px-3 py-2 font-medium">{t('purchases.register.colKind')}</th>
                <th className="px-3 py-2 font-medium">{t('purchases.register.colNumber')}</th>
                <th className="px-3 py-2 font-medium">{t('purchases.register.colSupplier')}</th>
                <th className="px-3 py-2 font-medium">{t('purchases.register.colStatus')}</th>
                <th className="px-3 py-2 text-right font-medium">
                  {t('purchases.register.colLines')}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t('purchases.register.colTotal')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows === null && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-xs text-muted-foreground">
                    {t('common.loading')}
                  </td>
                </tr>
              )}
              {rows?.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-xs text-muted-foreground">
                    {t('purchases.register.empty')}
                  </td>
                </tr>
              )}
              {rows?.map((r) => (
                <tr
                  key={`${r.kind}:${r.id}`}
                  onClick={() => open(r)}
                  className="cursor-pointer border-t hover:bg-accent/40"
                >
                  <td className="px-3 py-1.5 text-xs tabular-nums">{formatDate(r.date)}</td>
                  <td className="px-3 py-1.5 text-xs">
                    <span className="inline-flex items-center gap-1.5">
                      {r.kind === 'PO' ? (
                        <ShoppingCart className="h-3.5 w-3.5 text-muted-foreground" />
                      ) : (
                        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                      {t(`purchases.register.channel_${r.channel}`)}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 font-mono text-xs">
                    {r.number ?? '—'}
                    {r.supplierRef && (
                      <div className="text-[11px] text-muted-foreground">{r.supplierRef}</div>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-xs">{r.supplierName}</td>
                  <td className="px-3 py-1.5 text-xs">
                    <Badge variant="outline">
                      {r.kind === 'PO'
                        ? enumLabel('poStatus', r.status)
                        : t(`purchases.register.status_${r.status}`, { defaultValue: r.status })}
                    </Badge>
                    {r.orderId && (
                      <span className="ml-1 text-[11px] text-muted-foreground">
                        {t('purchases.register.forCustomer')}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right text-xs tabular-nums">
                    {r.lineCount}
                    <span className="text-muted-foreground"> · {r.units}</span>
                  </td>
                  <td className="px-3 py-1.5 text-right text-xs tabular-nums">
                    {r.totalHT == null ? '—' : formatEUR(r.totalHT)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detail && (
        <InvoiceDialog
          detail={detail}
          onClose={() => {
            setDetail(null);
            if (linked) {
              params.delete('invoice');
              setParams(params, { replace: true });
            }
          }}
        />
      )}
    </div>
  );
}

function InvoiceDialog({ detail, onClose }: { detail: InvoiceDetail; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div className="relative flex max-h-[88vh] w-full max-w-4xl flex-col rounded-lg bg-background shadow-xl">
        <header className="flex items-start justify-between border-b px-5 py-3">
          <div>
            <h2 className="text-lg font-semibold">
              {t('purchases.register.invoiceTitle', { number: detail.invoiceNumber ?? '—' })}
            </h2>
            <p className="text-xs text-muted-foreground">
              {detail.supplierName} ({detail.supplierCode})
              {detail.invoiceDate && ` · ${formatDate(detail.invoiceDate)}`}
              {` · ${t('purchases.register.receivedOn', { date: formatDate(detail.createdAt) })}`}
              {detail.importedBy && ` · ${detail.importedBy}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent"
            aria-label={t('common.close')}
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="flex-1 overflow-auto px-5 py-3">
          <table className="w-full border-collapse text-xs">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1.5 pr-3 font-medium">{t('purchases.register.colRef')}</th>
                <th className="py-1.5 pr-3 font-medium">{t('purchases.register.colBrand')}</th>
                <th className="py-1.5 pr-3 font-medium">
                  {t('purchases.register.colDesignation')}
                </th>
                <th className="py-1.5 pr-3 font-medium">{t('purchases.register.colProduct')}</th>
                <th className="py-1.5 pr-3 text-right font-medium">
                  {t('purchases.register.colQty')}
                </th>
                <th className="py-1.5 pr-3 text-right font-medium">
                  {t('purchases.register.colUnit')}
                </th>
                <th className="py-1.5 pr-3 text-right font-medium">
                  {t('purchases.register.colLineTotal')}
                </th>
                <th className="py-1.5 text-right font-medium">{t('purchases.register.colCost')}</th>
              </tr>
            </thead>
            <tbody>
              {detail.lines.map((l) => (
                <tr key={l.id} className="border-t">
                  <td className="py-1.5 pr-3 font-mono">{l.ref ?? '—'}</td>
                  <td className="py-1.5 pr-3">{l.brand ?? '—'}</td>
                  <td className="py-1.5 pr-3">{l.description ?? '—'}</td>
                  <td className="py-1.5 pr-3">
                    <span className="font-mono">{l.productSku ?? '—'}</span>
                    {l.productCreated && (
                      <Badge variant="outline" className="ml-1 px-1 py-0 text-[10px]">
                        {t('purchases.register.created')}
                      </Badge>
                    )}
                    {(l.poNumbers.length > 0 || l.alreadyInStock > 0) && (
                      <div className="text-[11px] text-muted-foreground">
                        {l.alreadyInStock > 0 &&
                          t('purchases.register.alreadyInStock', { count: l.alreadyInStock })}
                        {l.alreadyInStock > 0 && l.poNumbers.length > 0 && ' · '}
                        {l.poNumbers.join(', ')}
                      </div>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{l.quantity}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">
                    {formatEUR(l.unitPriceHT)}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">
                    {formatEUR(l.quantity * l.unitPriceHT)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {l.costAfter == null ? (
                      '—'
                    ) : (
                      <span
                        title={
                          l.priceAcceptedBy
                            ? t('purchases.register.priceAcceptedBy', {
                                name: l.priceAcceptedBy,
                                pct: l.variancePct,
                              })
                            : undefined
                        }
                      >
                        {l.costBefore == null ? '' : `${formatEUR(l.costBefore)} → `}
                        <strong>{formatEUR(l.costAfter)}</strong>
                        {l.varianceFlag && <span className="ml-1 text-warning">!</span>}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t font-semibold">
                <td colSpan={6} className="py-2 pr-3 text-right">
                  {t('purchases.register.totalHT')}
                </td>
                <td className="py-2 pr-3 text-right tabular-nums">
                  {detail.totalHT == null ? '—' : formatEUR(detail.totalHT)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
          <p className="mt-2 text-[11px] text-muted-foreground">
            {t('purchases.register.recordNote')}
          </p>
        </div>
      </div>
    </div>
  );
}
